import styled, { css } from 'styled-components';

import colors from '@/design/colors';
import { dashedBorder } from '@/design/borders';
import { HeadingSmall, Text } from '@/design/typography';

import CheckboxLabel from '@/components/CheckboxLabel';

const getItemTitleColor = ({
  elemState,
  itemState,
}: {
  elemState: 'indie' | 'hover';
  itemState?: 'unmarked' | 'marked';
}) => {
  if (elemState === 'hover') {
    return colors.purple100;
  }

  if (itemState === 'marked') {
    return colors.blackAlpha200;
  }

  return colors.blackAlpha100;
};

export const DefaultLayout = styled.div`
  /* Layout */
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: flex-start;
  justify-content: space-between;
`;

export const DefaultContent = styled.div`
  /* Layout */
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ItemContent = styled.div<{ expanded: boolean }>`
  /* Layout */
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-left: 32px;

  /* Collapse */
  ${({ expanded }) =>
    !expanded &&
    css`
      overflow: hidden;
      max-height: 26px; /* line-height(18) + padding(4 + 4) */
    `}

  p {
    /* Layout */
    padding: 4px 0;

    /* Typography */
    ${Text(14)}

    /* Colors */
    color: ${colors.gray300};

    ${({ expanded }) =>
      !expanded &&
      css`
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      `}
  }
`;

export const CardControl = styled(CheckboxLabel).attrs({
  size: 'small',
})`
  gap: 16px;

  ${({ readOnly }) =>
    readOnly &&
    css`
      /* Align description vertically with title, when has no control */
      ~ ${ItemContent} {
        margin-left: 0;
      }
    `}
`;

export const ItemTitle = styled.span`
  /* Typography */
  ${HeadingSmall(14)}
`;

export const ItemHeading = styled.div<{
  elemState: 'indie' | 'hover';
  itemState?: 'unmarked' | 'marked';
}>`
  /* Layout */
  display: flex;
  align-items: center;
  gap: 8px;

  > ${ItemTitle} {
    /* Colors */
    color: ${getItemTitleColor};
  }
`;

export const ActionArea = styled.div`
  /* Layout */
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

export const DefaultSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const DetailsContainer = styled.div`
  /* Layout */
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const DetailsHeading = styled.h2`
  /* Typography */
  ${HeadingSmall(18)}

  /* Colors */
  color: ${colors.blackAlpha200};
`;

export const BlockOverlay = styled.div`
  /* Overlay that blocks all interactions when rendered */
  position: absolute;
  inset: 0;
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.6);
  pointer-events: auto;
  z-index: 2;
`;

export const Container = styled.div<{
  itemState: 'unmarked' | 'marked';
  variant: 'default' | 'added';
  blocked: boolean;
}>`
  /* Layout */
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;

  /* Colors */
  background-color: ${({ itemState }) =>
    itemState === 'marked' ? colors.gray50 : colors.white};

  /* Borders */
  border-radius: 8px;

  ${({ variant }) =>
    variant === 'added'
      ? dashedBorder({
          color: colors.gray100,
          lineCap: 'round',
          dashArray: '8, 8',
          strokeWidth: 2,
          borderRadius: 8,
        })
      : css`
          border: 1px solid ${colors.gray100};
        `}
`;
